import { FunctionComponent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { useTheme } from '@mui/material/styles';

import HistoryIcon from '@mui/icons-material/History';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid
} from '@mui/material';

import {
  Page,
  StyledButton,
  Typography
} from '../../components/interface';

import { AlertSomethingWentWrong } from '../../components/alerts/SomethingWentWrong';

import { loginAtom } from '../../lib/auth';
import { fetch_all, fetch_one } from '../../lib/server_helper';

import { PAIN_ACCOUNTS } from './accounts';

const formatIban = (iban: string) => iban.replace(/(.{4})/g, '$1 ').trim();

type PainCreatorProps = {};

export const PainCreator: FunctionComponent<PainCreatorProps> = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [login] = useAtom(loginAtom);

  const [counts, setCounts] = useState<Record<string, number>>({});
  const [fileCounts, setFileCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [handleAllLoading, setHandleAllLoading] = useState<boolean>(false);
  const [serverErrorSnackOpen, setServerErrorSnackOpen] = useState<boolean>(false);

  const onSearch = () => {
    setLoading(true);
    let active = true;
    let done = 0;

    PAIN_ACCOUNTS.forEach((account) => {
      const settle = () => {
        done += 1;
        if (active && done === PAIN_ACCOUNTS.length * 2) setLoading(false);
      };

      fetch_all('/nl/v3/objects/fetch_all', {
        object: 'pain_row',
        fields: ['id'],
        per_page: 1,
        page: 0,
        filter: {
          advanced: {
            account_type: account.name.toLowerCase(),
            filter_done: 0
          }
        }
      }, (obs, complete_data) => {
        if (!active) return;
        let count = 0;
        if (typeof(complete_data?.data) === 'object' && 'count' in complete_data?.data) count = complete_data?.data?.count;
        setCounts((c) => ({...c, [account.name.toLowerCase()]: count}));
        settle();
      }, settle, login);

      fetch_all('/nl/v3/objects/fetch_all', {
        object: 'generated_pain',
        fields: ['id'],
        per_page: 1,
        page: 0,
        filter: {
          advanced: {
            account_type: account.name.toLowerCase()
          }
        }
      }, (obs, complete_data) => {
        if (!active) return;
        let count = 0;
        if (typeof(complete_data?.data) === 'object' && 'count' in complete_data?.data) count = complete_data?.data?.count;
        setFileCounts((c) => ({...c, [account.name.toLowerCase()]: count}));
        settle();
      }, settle, login);
    });

    return () => {active = false;};
  };

  useEffect(() => {
    if (!login) return;
    onSearch();
  }, [login]);

  const handleAll = () => {
    setHandleAllLoading(true);

    const pendingAccounts = PAIN_ACCOUNTS
      .map((account) => account.name.toLowerCase())
      .filter((key) => (counts[key] || 0) > 0);

    pendingAccounts.reduce((chain: Promise<void>, key) => {
      return chain.then(() => new Promise<void>((resolve, reject) => {
        fetch_one('/nl/v3/objects/custom_action', {
          object: 'generated_pain',
          class_action: 'custom_api_engage',
          account_type: key
        }, () => {
          resolve();
        }, (z) => {
          reject(z);
        }, login);
      }));
    }, Promise.resolve()).then(() => {
      setHandleAllLoading(false);
      navigate('/give_me_pain');
    }).catch(() => {
      setServerErrorSnackOpen(true);
      onSearch();
      setHandleAllLoading(false);
    });
  };

  const totalPending = () => PAIN_ACCOUNTS.reduce((sum, account) => sum + (counts[account.name.toLowerCase()] || 0), 0);

  const renderAccountBlock = (account: typeof PAIN_ACCOUNTS[0]) => {
    const key = account.name.toLowerCase();

    return (
      <Grid item xs={12} md={4} key={account.iban}>
        <Card sx={{
          height: '100%',
          borderLeft: `4px solid ${theme.palette.primary?.main}`,
          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.12)'
        }}>
          <CardContent sx={{padding: 3}}>
            <Box sx={{display: 'flex', alignItems: 'center', marginBottom: 1.5}}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 44,
                height: 44,
                borderRadius: 2,
                marginRight: 2,
                backgroundColor: theme.palette.primary?.main,
                color: 'white'
              }}>
                {account.icon}
              </Box>
              <Box>
                <Typography variant="h2" sx={{fontSize: '1.25rem'}}>{account.name}</Typography>
                <Typography variant="body2" sx={{color: theme.palette.primary_grey?.main}}>{account.description}</Typography>
              </Box>
            </Box>

            <Typography variant="body1" sx={{fontFamily: 'monospace', fontWeight: 'bold', letterSpacing: 1}}>
              {formatIban(account.iban)}
            </Typography>

            <Divider sx={{marginY: 2}} />

            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
              <Box sx={{display: 'flex', alignItems: 'center'}}>
                <ReceiptLongIcon sx={{marginRight: 1, color: theme.palette.primary_grey?.main}} />
                {loading
                  ? <CircularProgress size={16} sx={{marginLeft: 1}} />
                  : <StyledButton
                    label={`${counts[key] || 0} pending rows`}
                    id={`pending_rows_${key}`}
                    text
                    disabled={(counts[key] || 0) === 0}
                    sx={{padding: 0, marginLeft: -1}}
                    onClick={() => {
                      navigate(`/create_pain/rows/${key}`);
                    }}
                  />}
              </Box>
              <StyledButton
                label="Past files"
                id={`past_files_${key}`}
                startIcon={<HistoryIcon />}
                disabled={(fileCounts[key] || 0) === 0}
                onClick={() => {
                  navigate(`/create_pain/files/${key}`);
                }}
              />
            </Box>

            <StyledButton
              label="Add row"
              id={`add_row_${key}`}
              contained
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => {
                navigate('/create_pain/new', {state: {account_type: key}});
              }}
              sx={{width: '100%', marginTop: 2}}
            />
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <Page title="PAIN Creator" actions={<>
      {totalPending() > 0 && <StyledButton
        label="Handle all"
        id='handle_all'
        contained
        loading={handleAllLoading}
        startIcon={<PlayArrowIcon />}
        onClick={() => {
          handleAll();
        }}
        sx={{marginRight: 2}}
      />}
      <StyledButton
        label="Add row"
        id='add_row'
        contained
        startIcon={<AddCircleOutlineIcon />}
        onClick={() => {
          navigate('/create_pain/new');
        }}
        sx={{marginRight: 3}}
      />
    </>}>
      <Grid container spacing={3}>
        {PAIN_ACCOUNTS.map(renderAccountBlock)}
      </Grid>

      {!!serverErrorSnackOpen && <AlertSomethingWentWrong open={serverErrorSnackOpen} setOpen={(b) => {setServerErrorSnackOpen(b);}} />}
    </Page>
  );
}
